import { Component, HostListener, ViewChild, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MemberService } from '../member-service/MemberService ';
import { ActivatedRoute } from '@angular/router';

@Component({
  selector: 'app-progress-notes',
  templateUrl: './progress-notes.component.html',
  standalone: true,
  imports: [CommonModule, FormsModule, MatTableModule, MatPaginatorModule, MatPaginator],
  styleUrls: ['./progress-notes.component.css']
})
export class ProgressNotesComponent implements AfterViewInit {
  activeProgressTab: string = 'priority';
  memberId: string = '';
  showAddNotesForm: boolean = false;
  showProgressReviewForm: boolean = false;
  isEditingExistingNote: boolean = false;
  showSuccessModal: boolean = false;
  successModalSourceTab: string = 'priority'; // Track which tab we came from
  isCreatingNewNote: boolean = false; // Track if we're creating new or updating existing
  showValidationModal: boolean = false; // Validation modal flag
  validationMessage: string = ''; // Validation error message
  isViewingFromCompletedTab: boolean = false; // Track if viewing from Completed Progress Notes tab

  // Custom input values when "Other" is selected
  customMedicalCondition: string = '';
  customGoalCategories: { [key: number]: string } = {}; // Store custom category for each goal index

  // Maximum date for "Identified On" field (today's date)
  maxDate: string = '';

  // Validation error messages for date fields
  identifiedOnError: string = '';
  goalDateErrors: { [goalIndex: number]: string } = {};
  taskDateErrors: { [key: string]: string } = {}; // Key format: "goalIndex-taskIndex"

  // ViewChild references to paginators
  @ViewChild('paginator') paginator!: MatPaginator;
  @ViewChild('archivedPaginator') archivedPaginator!: MatPaginator;
  @ViewChild('completedPaginator') completedPaginator!: MatPaginator;

  // Table configuration for Priority List
  dataSource = new MatTableDataSource<any>([]);
  displayedColumns: string[] = ['HealthIssues', 'IdentifiedOn', 'AssignedTo', 'Priority', 'Actions'];

  // Table configuration for Archived List
  archivedDataSource = new MatTableDataSource<any>([]);

  // Table configuration for Old Progress Notes
  oldProgressNotesDataSource = new MatTableDataSource<any>([]);
  oldProgressNotesColumns: string[] = ['Date', 'Summary', 'Outcome', 'ActionPlan', 'AddedBy'];

  // Table configuration for Completed Progress Notes
  completedDataSource = new MatTableDataSource<any>([]);
  completedDisplayedColumns: string[] = ['HealthIssues', 'IdentifiedOn', 'AssignedTo', 'Priority', 'Actions'];

  // Pagination configuration
  pageSize: number = 10;
  totalRecords: number = 0;
  archivedTotalRecords: number = 0;
  oldProgressNotesTotalRecords: number = 0;
  completedTotalRecords: number = 0;

  // Form data
  progressNotesForm = {
    id: '', // Store progress note ID when editing
    complaints: '',
    medicalConditions: '',
    identifiedOn: '',
    goals: [
      {
        _id: '', // Store goal ID when editing
        goalToBeAchieved: '',
        goalPriorityLevel: 'High',
        goalCategory: '',
        goalStartDate: '',
        goalTargetDate: '',
        tasks: [
          {
            _id: '', // Store task ID when editing
            taskName: '',
            taskStatus: '',
            taskOwner: '',
            taskStartDate: '',
            taskEndDate: ''
          }
        ]
      }
    ]
  };

  // Dropdown options
  medicalConditionsOptions = [
    'Diabetes (Type 2)',
    'Prediabetes',
    'Insulin Resistance',
    'Weight Management',
    'Thyroid Disorders (Hypothyroidism, Hyperthyroidism, Hashimoto)',
    'PCOS / PCOD',
    'Fatty Liver (NAFLD, NASH)',
    'High Cholesterol / Dyslipidemia',
    'CKD',
    'Gout/ High uric acid',
    'Hypertension (High Blood Pressure)',
    'Coronary Artery Disease /CVD',
    'Atherosclerosis',
    'Post-Cardiac Event (Heart Attack, Bypass)',
    'Stroke',
    'Gastritis / Acid Reflux',
    'Irritable Bowel Syndrome (IBS) and IBD',
    'Constipation',
    'Bloating / Indigestion',
    'Gut Dysbiosis',
    'Lactose Intolerance',
    'Gluten intolerance',
    'Celiac',
    'Gall stones',
    'Kidney stones',
    'Stress / Anxiety',
    'Depression (Mild to Moderate)',
    'Sleep Disorders / Insomnia',
    'Emotional Eating',
    'Burnout / Work Stress',
    'ADD / ADHD',
    'Arthritis (Osteoarthritis, Rheumatoid Arthritis)',
    'Back Pain / Joint Pain',
    'Cervical',
    'Musculoskeletal & Spinal Disorders',
    'Menstrual Irregularities',
    'Menopause / Perimenopause',
    'Low Testosterone',
    'Fertility Support',
    'UTI',
    'Chronic Inflammation',
    'Autoimmune Conditions (mild/moderate)',
    'Vitamin/Mineral Deficiencies',
    'Retinopathy',
    'Nephropathy',
    'Neuropathy',
    'Diabetic Dermopathy',
    'Diabetic Foot Ulcers',
    'Bullosis Diabeticorum (Diabetic Blisters)',
    'Eczema',
    'Viral (Warts, Herpes)',
    'Vitiligo',
    'Psoriasis',
    'Hyperkeratosis',
    'Other'
  ];

  goalCategoryOptions = [
    'Clinical', 'Lifestyle', 'Nutrition', 'Exercise',
    'Mental Health', 'Medication', 'Monitoring', 'Other'
  ];

  taskStatusOptions = [
    'Not Started', 'Ongoing', 'Completed', 'On Hold', 'Cancelled'
  ];

  // Track which status dropdown is open
  openStatusDropdown: { goalIndex: number, taskIndex: number } | null = null;

  // Track which health issue dropdown is open
  openHealthIssueDropdown: string | null = null;

  // Progress Review Form data
  progressReviewForm = {
    id: '', // Store progress note ID
    currentHealthIssues: '',
    medicalConditions: '',
    identifiedOn: '',
    goals: [
      {
        _id: '', // Store goal ID
        goalsToBeAchieved: '',
        goalCategory: '',
        goalStartDate: '',
        goalEndDate: '',
        goalPriorityLevel: 'High',
        tasks: [
          {
            _id: '', // Store task ID
            task: '',
            taskStatus: '',
            taskOwner: '',
            taskStartDate: '',
            taskEndDate: ''
          }
        ]
      }
    ],
    progressSummary: ''
  };

  constructor(
    private memberService: MemberService,
    private route: ActivatedRoute
  ) { }

  ngOnInit(): void {
    // Initialize with Priority List tab selected
    this.activeProgressTab = 'priority';

    // Set max date to today for "Identified On" field
    const today = new Date();
    this.maxDate = today.toISOString().split('T')[0];

    // Get memberId from route
    this.memberId = this.route.snapshot.queryParamMap.get('id') || '';

    // Load custom options from localStorage
    this.loadCustomOptionsFromStorage();

    // Load member issues
    if (this.memberId) {
      this.loadMemberIssues();
      this.loadOldProgressNotes();
    }
  }

  ngAfterViewInit(): void {
    // Connect paginators to data sources for client-side pagination
    // This enables automatic pagination for Priority, Archived, and Completed tabs
    this.connectPaginators();
  }

  // Helper method to safely connect paginators to datasources
  private connectPaginators(): void {
    // Use setTimeout to ensure paginators are rendered (needed when switching tabs)
    setTimeout(() => {
      if (this.paginator) {
        this.dataSource.paginator = this.paginator;
      }
      if (this.archivedPaginator) {
        this.archivedDataSource.paginator = this.archivedPaginator;
      }
      if (this.completedPaginator) {
        this.completedDataSource.paginator = this.completedPaginator;
      }
    }, 0);
  }

  private loadCustomOptionsFromStorage(): void {
    try {
      // Load custom medical conditions
      const savedMedicalConditions = localStorage.getItem('customMedicalConditions');
      if (savedMedicalConditions) {
        const customConditions = JSON.parse(savedMedicalConditions);
        // Add custom conditions to the options if not already present
        customConditions.forEach((condition: string) => {
          if (!this.medicalConditionsOptions.includes(condition)) {
            // Insert before "Other" option
            const otherIndex = this.medicalConditionsOptions.indexOf('Other');
            if (otherIndex !== -1) {
              this.medicalConditionsOptions.splice(otherIndex, 0, condition);
            }
          }
        });
      }

      // Load custom goal categories
      const savedGoalCategories = localStorage.getItem('customGoalCategories');
      if (savedGoalCategories) {
        const customCategories = JSON.parse(savedGoalCategories);
        // Add custom categories to the options if not already present
        customCategories.forEach((category: string) => {
          if (!this.goalCategoryOptions.includes(category)) {
            // Insert before "Other" option
            const otherIndex = this.goalCategoryOptions.indexOf('Other');
            if (otherIndex !== -1) {
              this.goalCategoryOptions.splice(otherIndex, 0, category);
            }
          }
        });
      }
    } catch (error) {
      console.error('Error loading custom options from localStorage:', error);
    }
  }

  private saveCustomMedicalConditionToStorage(condition: string): void {
    try {
      const savedConditions = localStorage.getItem('customMedicalConditions');
      let customConditions: string[] = savedConditions ? JSON.parse(savedConditions) : [];

      if (!customConditions.includes(condition)) {
        customConditions.push(condition);
        localStorage.setItem('customMedicalConditions', JSON.stringify(customConditions));
      }
    } catch (error) {
      console.error('Error saving custom medical condition to localStorage:', error);
    }
  }

  private saveCustomGoalCategoryToStorage(category: string): void {
    try {
      const savedCategories = localStorage.getItem('customGoalCategories');
      let customCategories: string[] = savedCategories ? JSON.parse(savedCategories) : [];

      if (!customCategories.includes(category)) {
        customCategories.push(category);
        localStorage.setItem('customGoalCategories', JSON.stringify(customCategories));
      }
    } catch (error) {
      console.error('Error saving custom goal category to localStorage:', error);
    }
  }

  loadMemberIssues(): void {
    this.memberService.getMemberIssues(this.memberId).subscribe({
      next: (response) => {
        if (response && response.result) {
          const mappedData = this.mapResponseToTableData(response.result);

          // Separate priority, archived, and completed items
          // Priority: isCompleted > isArchived > default (priority)
          const completedItems = mappedData.filter((item: any) => item.isCompleted);
          const archivedItems = mappedData.filter((item: any) => item.isArchived && !item.isCompleted);
          const priorityItems = mappedData.filter((item: any) => !item.isArchived && !item.isCompleted);

          // Sort all items by Identified On date (latest first)
          this.sortByIdentifiedOn(priorityItems);
          this.sortByIdentifiedOn(archivedItems);
          this.sortByIdentifiedOn(completedItems);

          this.dataSource.data = priorityItems;
          this.archivedDataSource.data = archivedItems;
          this.completedDataSource.data = completedItems;

          this.totalRecords = priorityItems.length;
          this.archivedTotalRecords = archivedItems.length;
          this.completedTotalRecords = completedItems.length;

          // Reconnect paginators after data is loaded (in case they were initialized after ngAfterViewInit)
          this.connectPaginators();
        }
      },
      error: (error) => {
        console.error('Error loading member issues:', error);
        console.log('API endpoint: MemberIssues/GetByMemberId - Check if backend is running and endpoint exists.');
      }
    });
  }

  // Helper method to sort items by Identified On date (latest first)
  private sortByIdentifiedOn(items: any[]): void {
    items.sort((a, b) => {
      // Handle null or invalid dates
      const dateA = a.identifiedOn ? new Date(a.identifiedOn).getTime() : 0;
      const dateB = b.identifiedOn ? new Date(b.identifiedOn).getTime() : 0;

      // Sort in descending order (latest first)
      return dateB - dateA;
    });
  }

  loadOldProgressNotes(pageNumber: number = 1): void {
    this.memberService.getProgressPlan(this.memberId, pageNumber).subscribe({
      next: (response: any) => {
        if (response && response.results) {
          const mappedData = this.mapOldProgressNotesToTableData(response.results);
          this.oldProgressNotesDataSource.data = mappedData;
          this.oldProgressNotesTotalRecords = response.totalCount || mappedData.length;
        }
      },
      error: (error) => {
        console.error('Error loading old progress notes:', error);
        console.log('API endpoint: progressNotes/GetProgressNotesByMemberId - Check if backend is running and endpoint exists.');
      }
    });
  }

  mapOldProgressNotesToTableData(data: any[]): any[] {
    return data.map(item => {
      // Combine first name and last name for Added By column
      const addedByName = item.userFirstName && item.userLastName
        ? `${item.userFirstName} ${item.userLastName}`
        : '-';

      return {
        date: item.createdDateTime ? this.formatDate(item.createdDateTime) : '-',
        summary: item.callSummary || '-',
        outcome: item.outCome || '-',
        actionPlan: item.planOfAction || '-',
        addedBy: addedByName
      };
    });
  }

  formatDate(dateString: string): string {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';

      return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } catch (error) {
      console.error('Error formatting date:', error);
      return '-';
    }
  }

  mapResponseToTableData(data: any[]): any[] {
    return data.map(item => {
      // Get the first goal and first task if they exist
      const firstGoal = item.goals && item.goals.length > 0 ? item.goals[0] : null;
      const firstTask = firstGoal && firstGoal.tasks && firstGoal.tasks.length > 0 ? firstGoal.tasks[0] : null;

      return {
        id: item.id,
        healthIssues: item.complaints || '-',
        identifiedOn: item.createdDate ? new Date(item.createdDate) : null,
        assignedTo: firstTask ? firstTask.taskOwner : '-',
        status: firstTask ? firstTask.taskStatus : '-',
        priority: firstGoal ? firstGoal.goalPriorityLevel : '-',
        isArchived: item.isArchived || false,
        isCompleted: item.isCompleted || false,
        rawData: item // Store original data for detailed view
      };
    });
  }

  selectProgressTab(tab: string) {
    this.activeProgressTab = tab;
    // Hide the forms when switching tabs to show the table
    this.showAddNotesForm = false;
    this.showProgressReviewForm = false;
    // Reconnect paginators when switching tabs (needed for tabs with *ngIf)
    this.connectPaginators();
  }

  pageChangeEvent(page: any) {
    // Pagination logic for Priority List
    console.log('Priority List - Page changed:', page);
  }

  archivedPageChangeEvent(page: any) {
    // Pagination logic for Archived List
    console.log('Archived List - Page changed:', page);
  }

  oldProgressNotesPageChangeEvent(page: any) {
    // Pagination logic for Old Progress Notes
    console.log('Old Progress Notes - Page changed:', page);
    // Load the new page data (pageIndex is 0-based, but API expects 1-based page numbers)
    const pageNumber = page.pageIndex + 1;
    this.loadOldProgressNotes(pageNumber);
  }

  completedPageChangeEvent(page: any) {
    // Pagination logic for Completed Progress Notes
    console.log('Completed Progress Notes - Page changed:', page);
  }

  moveToArchive(row: any): void {
    console.log('Moving to archive:', row);

    if (!row.rawData || !row.rawData.id) {
      console.error('Cannot archive: missing record ID');
      alert('Cannot archive this record. Missing record ID.');
      return;
    }

    const updateData = {
      isArchived: true,
      modifiedDate: new Date().toISOString()
    };

    this.memberService.updateProgressNotes(row.rawData.id, updateData).subscribe({
      next: (response) => {
        console.log('Item archived successfully via API:', response);

        // Update local data after successful API call
        if (row.rawData) {
          row.rawData.isArchived = true;
          row.isArchived = true;
        }

        // Remove from Priority List and add to Archived List
        const priorityData = [...this.dataSource.data];
        const archivedData = [...this.archivedDataSource.data];

        // Find and remove from priority list
        const indexToRemove = priorityData.findIndex(item => item.id === row.id);
        if (indexToRemove !== -1) {
          priorityData.splice(indexToRemove, 1);
        }

        // Add to archived list
        archivedData.push(row);

        // Sort by Identified On (latest first)
        this.sortByIdentifiedOn(priorityData);
        this.sortByIdentifiedOn(archivedData);

        // Update the data sources
        this.dataSource.data = priorityData;
        this.archivedDataSource.data = archivedData;

        // Update record counts
        this.totalRecords = priorityData.length;
        this.archivedTotalRecords = archivedData.length;

        // Reconnect paginators after data update
        this.connectPaginators();

        console.log('Priority items:', this.totalRecords, 'Archived items:', this.archivedTotalRecords);
      },
      error: (error) => {
        console.error('Error archiving item:', error);
        alert('Failed to archive item. Please try again.');
      }
    });
  }

  // Track the current row being viewed for completion
  currentViewedRow: any = null;

  // Store original data for comparison (to track edited fields only)
  originalProgressNoteData: any = null;

  // Track goals/tasks removed during edit - send _id + message so backend can delete them
  deletedGoalsToSend: { _id: string; message: string }[] = [];
  deletedTasksToSend: { _id: string; message: string }[] = [];

  viewDetail(row: any, fromCompletedTab: boolean = false): void {
    console.log('View detail:', row);
    this.showAddNotesForm = false;
    this.showProgressReviewForm = true;

    // Set flag to indicate we're updating existing note (not creating new)
    this.isCreatingNewNote = false;
    this.isEditingExistingNote = true;

    // Track if viewing from Completed tab
    this.isViewingFromCompletedTab = fromCompletedTab;

    // Store current row for completion action
    this.currentViewedRow = row;

    // Store original data for change tracking
    this.originalProgressNoteData = JSON.parse(JSON.stringify(row.rawData));

    // Clear any previously tracked deletions when opening a note
    this.deletedGoalsToSend = [];
    this.deletedTasksToSend = [];

    // Populate review form with row data
    // Handle medical conditions display - show descriptive text if value is "Other"
    let medicalConditionsDisplayValue = row.rawData?.medicalConditions || '';
    if (medicalConditionsDisplayValue === 'Other') {
      medicalConditionsDisplayValue = 'Custom Medical Condition';
    }

    // Store the progress note ID
    const progressNoteId = row.rawData?.id || '';

    this.progressReviewForm = {
      id: progressNoteId, // Store progress note ID
      currentHealthIssues: row.healthIssues || '',
      medicalConditions: medicalConditionsDisplayValue,
      identifiedOn: this.isValidDate(row.identifiedOn) ? new Date(row.identifiedOn).toISOString().split('T')[0] : '',
      goals: row.rawData?.goals?.map((goal: any) => {
        // Handle goal category display - show descriptive text if value is "Other"
        let goalCategoryDisplayValue = goal.goalCategory || '';
        if (goalCategoryDisplayValue === 'Other') {
          goalCategoryDisplayValue = 'Custom Goal Category';
        }

        // Store goal ID
        const goalId = goal._id || '';

        return {
          _id: goalId, // Store goal ID
          goalsToBeAchieved: goal.goalToBeAchieved || '',
          goalCategory: goalCategoryDisplayValue,
          goalStartDate: this.isValidDate(goal.goalStartDate) ? new Date(goal.goalStartDate).toISOString().split('T')[0] : '',
          goalEndDate: this.isValidDate(goal.goalTargetDate) ? new Date(goal.goalTargetDate).toISOString().split('T')[0] : '',
          goalPriorityLevel: goal.goalPriorityLevel || 'High',
          tasks: goal.tasks?.map((task: any) => {
            // Store task ID
            const taskId = task._id || '';
            return {
              _id: taskId, // Store task ID
              task: task.taskName || '',
              taskStatus: task.taskStatus || '',
              taskOwner: task.taskOwner || '',
              taskStartDate: this.isValidDate(task.taskStartDate) ? new Date(task.taskStartDate).toISOString().split('T')[0] : '',
              taskEndDate: this.isValidDate(task.taskEndDate) ? new Date(task.taskEndDate).toISOString().split('T')[0] : ''
            };
          }) || [{ _id: '', task: '', taskStatus: '', taskOwner: '', taskStartDate: '', taskEndDate: '' }]
        };
      }) || [{
        _id: '',
        goalsToBeAchieved: '',
        goalCategory: '',
        goalStartDate: '',
        goalEndDate: '',
        goalPriorityLevel: 'High',
        tasks: [{ _id: '', task: '', taskStatus: '', taskOwner: '', taskStartDate: '', taskEndDate: '' }]
      }],
      progressSummary: row.rawData?.progressSummary || ''
    };
  }

  restoreFromArchive(row: any): void {
    console.log('Restoring from archive:', row);

    if (!row.rawData || !row.rawData.id) {
      console.error('Cannot restore: missing record ID');
      alert('Cannot restore this record. Missing record ID.');
      return;
    }

    const updateData = {
      isArchived: false,
      modifiedDate: new Date().toISOString()
    };

    this.memberService.updateProgressNotes(row.rawData.id, updateData).subscribe({
      next: (response) => {
        console.log('Item restored successfully via API:', response);

        // Update local data after successful API call
        if (row.rawData) {
          row.rawData.isArchived = false;
          row.isArchived = false;
        }

        // Remove from Archived List and add to Priority List
        const priorityData = [...this.dataSource.data];
        const archivedData = [...this.archivedDataSource.data];

        // Find and remove from archived list
        const indexToRemove = archivedData.findIndex(item => item.id === row.id);
        if (indexToRemove !== -1) {
          archivedData.splice(indexToRemove, 1);
        }

        // Add to priority list
        priorityData.push(row);

        // Sort by Identified On (latest first)
        this.sortByIdentifiedOn(priorityData);
        this.sortByIdentifiedOn(archivedData);

        // Update the data sources
        this.dataSource.data = priorityData;
        this.archivedDataSource.data = archivedData;

        // Update record counts
        this.totalRecords = priorityData.length;
        this.archivedTotalRecords = archivedData.length;

        // Reconnect paginators after data update
        this.connectPaginators();

        console.log('Priority items:', this.totalRecords, 'Archived items:', this.archivedTotalRecords);
      },
      error: (error) => {
        console.error('Error restoring item:', error);
        alert('Failed to restore item. Please try again.');
      }
    });
  }

  addNewNotes(): void {
    console.log('Add New Notes clicked - showing form');
    this.isEditingExistingNote = false;
    this.isCreatingNewNote = true;
    this.showAddNotesForm = true;
    this.originalProgressNoteData = null; // Clear original data for new notes
    this.progressReviewForm.progressSummary = ''; // Keep progress summary empty for new note
    this.resetForm();
  }

  cancelAddNotes(): void {
    console.log('Cancel clicked - hiding form');
    this.isEditingExistingNote = false;
    this.isCreatingNewNote = false;
    this.showAddNotesForm = false;
    this.resetForm();
  }

  resetForm(): void {
    this.deletedGoalsToSend = [];
    this.deletedTasksToSend = [];
    this.progressNotesForm = {
      id: '',
      complaints: '',
      medicalConditions: '',
      identifiedOn: '',
      goals: [
        {
          _id: '',
          goalToBeAchieved: '',
          goalPriorityLevel: 'High',
          goalCategory: '',
          goalStartDate: '',
          goalTargetDate: '',
          tasks: [
            {
              _id: '',
              taskName: '',
              taskStatus: '',
              taskOwner: '',
              taskStartDate: '',
              taskEndDate: ''
            }
          ]
        }
      ]
    };
    // Clear custom values
    this.customMedicalCondition = '';
    this.customGoalCategories = {};

    // Clear all validation errors
    this.identifiedOnError = '';
    this.goalDateErrors = {};
    this.taskDateErrors = {};
  }

  addTask(goalIndex: number): void {
    this.progressNotesForm.goals[goalIndex].tasks.push({
      _id: '', // New tasks don't have IDs
      taskName: '',
      taskStatus: '',
      taskOwner: '',
      taskStartDate: '',
      taskEndDate: ''
    });
  }

  deleteTask(goalIndex: number, taskIndex: number): void {
    if (this.progressNotesForm.goals[goalIndex].tasks.length > 1) {
      const task = this.progressNotesForm.goals[goalIndex].tasks[taskIndex];
      if (task._id) {
        this.deletedTasksToSend.push({ _id: task._id, message: 'delete this task' });
      }
      this.progressNotesForm.goals[goalIndex].tasks.splice(taskIndex, 1);
    }
  }

  addGoal(): void {
    console.log('Add Goal clicked - adding new goal');
    this.progressNotesForm.goals.push({
      _id: '', // New goals don't have IDs
      goalToBeAchieved: '',
      goalPriorityLevel: 'High',
      goalCategory: '',
      goalStartDate: '',
      goalTargetDate: '',
      tasks: [
        {
          _id: '', // New tasks don't have IDs
          taskName: '',
          taskStatus: '',
          taskOwner: '',
          taskStartDate: '',
          taskEndDate: ''
        }
      ]
    });
    // No need to initialize customGoalCategories for the new goal
  }

  deleteGoal(goalIndex: number): void {
    if (this.progressNotesForm.goals.length > 1) {
      const goal = this.progressNotesForm.goals[goalIndex];
      if (goal._id) {
        this.deletedGoalsToSend.push({ _id: goal._id, message: 'delete this goal' });
      }
      (goal.tasks || []).forEach((task: any) => {
        if (task._id) {
          this.deletedTasksToSend.push({ _id: task._id, message: 'delete this task' });
        }
      });
      this.progressNotesForm.goals.splice(goalIndex, 1);

      // Reindex the customGoalCategories after deletion
      const updatedCategories: { [key: number]: string } = {};
      Object.keys(this.customGoalCategories).forEach(key => {
        const index = parseInt(key);
        if (index < goalIndex) {
          // Keep the same index for goals before the deleted one
          updatedCategories[index] = this.customGoalCategories[index];
        } else if (index > goalIndex) {
          // Shift down the index for goals after the deleted one
          updatedCategories[index - 1] = this.customGoalCategories[index];
        }
        // Skip the deleted goal index
      });
      this.customGoalCategories = updatedCategories;
    }
  }

  moveToReview(): void {
    console.log('Move to Review clicked', this.progressNotesForm);

    // Validate required date fields
    const validationErrors: string[] = [];

    // Check Identified On date
    if (!this.progressNotesForm.identifiedOn) {
      validationErrors.push('Identified On');
    }

    // Validate custom medical condition if "Other" is selected
    if (this.progressNotesForm.medicalConditions === 'Other' && !this.customMedicalCondition.trim()) {
      validationErrors.push('Custom Medical Condition (you selected "Other")');
    }

    // Check each goal's dates and tasks
    this.progressNotesForm.goals.forEach((goal, goalIndex) => {
      if (!goal.goalStartDate) {
        validationErrors.push(`Goal ${goalIndex + 1} Start Date`);
      }
      if (!goal.goalTargetDate) {
        validationErrors.push(`Goal ${goalIndex + 1} Target Date`);
      }

      // Validate custom goal category if "Other" is selected
      if (goal.goalCategory === 'Other' && !this.customGoalCategories[goalIndex]?.trim()) {
        validationErrors.push(`Goal ${goalIndex + 1} Custom Category (you selected "Other")`);
      }

      // Check task dates based on status
      goal.tasks.forEach((task, taskIndex) => {
        if (task.taskStatus === 'Ongoing') {
          // For Ongoing status, only start date is required
          if (!task.taskStartDate) {
            validationErrors.push(`Goal ${goalIndex + 1} - Task ${taskIndex + 1} Start Date`);
          }
        } else {
          // For other statuses, both start and end dates are required
          if (!task.taskStartDate) {
            validationErrors.push(`Goal ${goalIndex + 1} - Task ${taskIndex + 1} Start Date`);
          }
          if (!task.taskEndDate) {
            validationErrors.push(`Goal ${goalIndex + 1} - Task ${taskIndex + 1} End Date`);
          }
        }
      });
    });

    // If there are validation errors, show validation modal
    if (validationErrors.length > 0) {
      this.validationMessage = `Please fill in the following required fields:\n\n${validationErrors.join('\n')}`;
      this.showValidationModal = true;
      return;
    }

    // Get existing progress summary
    let existingProgressSummary = '';
    
    // If we're editing an existing note, prefer user's edits on review form (e.g. if they edited progress summary then went to edit page)
    if (this.progressNotesForm.id && this.originalProgressNoteData) {
      existingProgressSummary = (this.progressReviewForm.progressSummary != null ? this.progressReviewForm.progressSummary : this.originalProgressNoteData.progressSummary) || '';
      console.log('Using progress summary (ID:', this.progressNotesForm.id, '):', existingProgressSummary);
    }
    // When creating new note, keep progress summary empty initially
    else if (!this.progressNotesForm.id && !this.originalProgressNoteData) {
      existingProgressSummary = '';
    }
    // If coming from review form (navigating back from review), preserve review form's progress summary
    else if (this.progressReviewForm.progressSummary) {
      existingProgressSummary = this.progressReviewForm.progressSummary;
      console.log('Preserving progress summary from review form:', existingProgressSummary);
    }
    // Otherwise, try to find record by ID if available
    else if (this.progressNotesForm.id || this.progressReviewForm.id) {
      const noteId = this.progressNotesForm.id || this.progressReviewForm.id;
      const allData = [...this.dataSource.data, ...this.archivedDataSource.data, ...this.completedDataSource.data];
      const matchingRecord = allData.find(item => item.rawData?.id === noteId);

      if (matchingRecord && matchingRecord.rawData && matchingRecord.rawData.progressSummary) {
        existingProgressSummary = matchingRecord.rawData.progressSummary;
        console.log('Found existing progress summary by ID:', noteId, ':', existingProgressSummary);
      } else {
        console.log('No existing progress summary found for ID:', noteId);
      }
    } else {
      console.log('No progress note ID available - creating new note');
    }

    // Convert Create New Progress Notes data to Progress Review format
    // Handle medical conditions display - show custom value or "Custom Medical Condition" if "Other"
    let medicalConditionsDisplayValue = this.progressNotesForm.medicalConditions || '';
    if (medicalConditionsDisplayValue === 'Other' && this.customMedicalCondition.trim()) {
      medicalConditionsDisplayValue = this.customMedicalCondition.trim();
    } else if (medicalConditionsDisplayValue === 'Other') {
      medicalConditionsDisplayValue = 'Custom Medical Condition';
    }

    this.progressReviewForm = {
      id: this.progressNotesForm.id || this.progressReviewForm.id || '', // Preserve progress note ID from edit form or existing review form
      currentHealthIssues: this.progressNotesForm.complaints || '',
      medicalConditions: medicalConditionsDisplayValue,
      identifiedOn: this.progressNotesForm.identifiedOn || '',
      goals: this.progressNotesForm.goals.map((goal, goalIndex) => {
        // Handle goal category display - show custom value or "Custom Goal Category" if "Other"
        let goalCategoryDisplayValue = goal.goalCategory || '';
        if (goalCategoryDisplayValue === 'Other' && this.customGoalCategories[goalIndex]?.trim()) {
          goalCategoryDisplayValue = this.customGoalCategories[goalIndex].trim();
        } else if (goalCategoryDisplayValue === 'Other') {
          goalCategoryDisplayValue = 'Custom Goal Category';
        }

        // Preserve goal ID from edit form
        const goalId = goal._id || '';

        return {
          _id: goalId, // Preserve goal ID from edit form
          goalsToBeAchieved: goal.goalToBeAchieved || '',
          goalCategory: goalCategoryDisplayValue,
          goalStartDate: goal.goalStartDate || '',
          goalEndDate: goal.goalTargetDate || '',
          goalPriorityLevel: goal.goalPriorityLevel || 'High',
          tasks: goal.tasks.map((task) => {
            // Preserve task ID from edit form
            const taskId = task._id || '';
            return {
              _id: taskId, // Preserve task ID from edit form
              task: task.taskName || '',
              taskStatus: task.taskStatus || '',
              taskOwner: task.taskOwner || '',
              taskStartDate: task.taskStartDate || '',
              taskEndDate: task.taskEndDate || ''
            };
          })
        };
      }),
      progressSummary: existingProgressSummary
    };

    // Set flag to indicate if we're creating new or updating existing
    this.isCreatingNewNote = !this.isEditingExistingNote;

    // Show progress review form
    this.showAddNotesForm = false;
    this.showProgressReviewForm = true;
  }

  // Close validation modal
  closeValidationModal(): void {
    this.showValidationModal = false;
    this.validationMessage = '';
  }

  setGoalPriority(goalIndex: number, priority: string): void {
    this.progressNotesForm.goals[goalIndex].goalPriorityLevel = priority;
  }

  // Progress Review Form methods
  enableEditMode(): void {
    console.log('Edit button clicked - Converting to edit mode');

    // Convert Progress Review data back to Create New Progress Notes format
    const medicalConditionsValue = this.progressReviewForm.medicalConditions || '';

    // Store progress note ID when converting to edit mode
    this.progressNotesForm.id = this.progressReviewForm.id || '';

    // Check if medical condition is "Custom Medical Condition" (display text for "Other") or a custom value not in standard options
    if (medicalConditionsValue === 'Custom Medical Condition') {
      this.progressNotesForm.complaints = this.progressReviewForm.currentHealthIssues || '';
      this.progressNotesForm.medicalConditions = 'Other';
      this.progressNotesForm.identifiedOn = this.progressReviewForm.identifiedOn || '';
      this.progressNotesForm.goals = [];
      this.customMedicalCondition = '';
    } else if (medicalConditionsValue && !this.medicalConditionsOptions.includes(medicalConditionsValue)) {
      this.progressNotesForm.complaints = this.progressReviewForm.currentHealthIssues || '';
      this.progressNotesForm.medicalConditions = 'Other';
      this.progressNotesForm.identifiedOn = this.progressReviewForm.identifiedOn || '';
      this.progressNotesForm.goals = [];
      this.customMedicalCondition = medicalConditionsValue;
    } else {
      this.progressNotesForm.complaints = this.progressReviewForm.currentHealthIssues || '';
      this.progressNotesForm.medicalConditions = medicalConditionsValue;
      this.progressNotesForm.identifiedOn = this.progressReviewForm.identifiedOn || '';
      this.progressNotesForm.goals = [];
    }

    // Process goals and check for custom categories
    // Preserve goal and task IDs when converting back to edit form
    this.progressNotesForm.goals = this.progressReviewForm.goals.map((goal, index) => {
      const goalCategoryValue = goal.goalCategory || '';
      const goalId = goal._id || ''; // Preserve goal ID

      // Check if goal category is "Custom Goal Category" (display text for "Other") or a custom value not in standard options
      if (goalCategoryValue === 'Custom Goal Category') {
        this.customGoalCategories[index] = '';
        return {
          _id: goalId, // Store goal ID in edit form
          goalToBeAchieved: goal.goalsToBeAchieved || '',
          goalPriorityLevel: goal.goalPriorityLevel || 'High',
          goalCategory: 'Other',
          goalStartDate: goal.goalStartDate || '',
          goalTargetDate: goal.goalEndDate || '',
          tasks: goal.tasks.map(task => ({
            _id: task._id || '', // Store task ID in edit form
            taskName: task.task || '',
            taskStatus: task.taskStatus || '',
            taskOwner: task.taskOwner || '',
            taskStartDate: task.taskStartDate || '',
            taskEndDate: task.taskEndDate || ''
          }))
        };
      } else if (goalCategoryValue && !this.goalCategoryOptions.includes(goalCategoryValue)) {
        this.customGoalCategories[index] = goalCategoryValue;
        return {
          _id: goalId, // Store goal ID in edit form
          goalToBeAchieved: goal.goalsToBeAchieved || '',
          goalPriorityLevel: goal.goalPriorityLevel || 'High',
          goalCategory: 'Other',
          goalStartDate: goal.goalStartDate || '',
          goalTargetDate: goal.goalEndDate || '',
          tasks: goal.tasks.map(task => ({
            _id: task._id || '', // Store task ID in edit form
            taskName: task.task || '',
            taskStatus: task.taskStatus || '',
            taskOwner: task.taskOwner || '',
            taskStartDate: task.taskStartDate || '',
            taskEndDate: task.taskEndDate || ''
          }))
        };
      } else {
        return {
          _id: goalId, // Store goal ID in edit form
          goalToBeAchieved: goal.goalsToBeAchieved || '',
          goalPriorityLevel: goal.goalPriorityLevel || 'High',
          goalCategory: goalCategoryValue,
          goalStartDate: goal.goalStartDate || '',
          goalTargetDate: goal.goalEndDate || '',
          tasks: goal.tasks.map(task => ({
            _id: task._id || '', // Store task ID in edit form
            taskName: task.task || '',
            taskStatus: task.taskStatus || '',
            taskOwner: task.taskOwner || '',
            taskStartDate: task.taskStartDate || '',
            taskEndDate: task.taskEndDate || ''
          }))
        };
      }
    });

    // Switch to edit mode
    this.isEditingExistingNote = true;
    this.showProgressReviewForm = false;
    this.showAddNotesForm = true;
  }

  saveProgressReview(): void {
    console.log('Saving progress review:', this.progressReviewForm);

    if (this.isCreatingNewNote) {
      // Create new progress notes
      this.createNewProgressNotesFromReview();
    } else {
      // Try to update existing progress notes first, fallback to create if not found
      this.tryUpdateOrCreateProgressNotes();
    }
  }

  private tryUpdateOrCreateProgressNotes(): void {
    console.log('Attempting to update or create progress notes');

    // Use the progress note ID directly from the form (not matching by complaints/date)
    const progressNoteId = this.progressReviewForm.id;

    if (progressNoteId) {
      console.log('Found progress note ID in form, attempting to update:', progressNoteId);
      this.updateProgressNotesWithId(progressNoteId);
    } else {
      console.log('No progress note ID found in form, creating new progress notes');
      this.createNewProgressNotesFromReview();
    }
  }

  private updateProgressNotesWithId(recordId: string): void {
    console.log('Updating progress notes with ID:', recordId);

    // Prepare complete update data from progress review form
    const updateData = this.prepareProgressNotesDataFromReview();

    this.memberService.updateProgressNotes(recordId, updateData).subscribe({
      next: (response) => {
        console.log('Progress notes updated successfully:', response);

        // Clear deleted tracking so we don't resend on next edit
        this.deletedGoalsToSend = [];
        this.deletedTasksToSend = [];

        // Store which tab we came from for navigation
        this.successModalSourceTab = this.activeProgressTab;

        // Show success modal
        this.showSuccessModal = true;

        // Reload data to show updated information
        this.loadMemberIssues();
      },
      error: (error) => {
        console.error('Error updating progress notes:', error);
        console.log('Update failed. Attempting to create new progress notes instead.');
        // Fallback to create new progress notes if update fails
        this.createNewProgressNotesFromReview();
      }
    });
  }

  private createNewProgressNotesFromReview(): void {
    console.log('Creating new progress notes from review form');

    // Prepare data for API call from progress review form
    const progressNotesData = this.prepareProgressNotesDataFromReview();

    this.memberService.saveProgressNotes(progressNotesData).subscribe({
      next: (response) => {
        console.log('Progress notes created successfully:', response);

        // Store which tab we came from for navigation
        this.successModalSourceTab = this.activeProgressTab;

        // Show success modal
        this.showSuccessModal = true;

        // Reload data to show the new note
        this.loadMemberIssues();
      },
      error: (error) => {
        console.error('Error creating progress notes:', error);
        alert('Failed to save progress notes. Please try again.');
      }
    });
  }

  // Helper method to check if date is valid (not null, not 0001-01-01, etc.)
  private isValidDate(dateString: string | null | undefined): boolean {
    if (!dateString) {
      return false;
    }
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return false;
      }
      // Check for invalid dates like 0001-01-01
      if (date.getFullYear() < 1900) {
        return false;
      }
      return true;
    } catch (error) {
      return false;
    }
  }

  // Helper method to convert date string to ISO 8601 format
  private convertToISO8601(dateString: string): string | null {
    if (!dateString) {
      return null;
    }
    try {
      const date = new Date(dateString);
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return null;
      }
      // Check for invalid dates like 0001-01-01
      if (date.getFullYear() < 1900) {
        return null;
      }
      // Convert to ISO 8601 format: 2025-06-10T00:00:00.000+00:00
      return date.toISOString();
    } catch (error) {
      console.error('Error converting date to ISO 8601:', error);
      return null;
    }
  }

  // Helper method to normalize dates for comparison (returns YYYY-MM-DD format)
  private normalizeDateForComparison(dateString: string | null | undefined): string {
    if (!dateString) {
      return '';
    }
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime()) || date.getFullYear() < 1900) {
        return '';
      }
      return date.toISOString().split('T')[0];
    } catch (error) {
      return '';
    }
  }

  private prepareProgressNotesDataFromReview(): any {
    const currentDate = new Date().toISOString();

    // If creating new note, send all fields
    if (!this.progressReviewForm.id || !this.originalProgressNoteData) {
      return this.prepareNewProgressNotePayload();
    }

    // For updates, only send changed fields
    const payload: any = {
      modifiedDate: currentDate
    };

    // Handle custom medical condition - add to options if "Other" was selected and custom value provided
    let medicalConditionsValue = this.progressReviewForm.medicalConditions || '';
    if (medicalConditionsValue === 'Other' && this.customMedicalCondition.trim()) {
      const trimmedCondition = this.customMedicalCondition.trim();
      // Add to options if not already present
      if (!this.medicalConditionsOptions.includes(trimmedCondition)) {
        const otherIndex = this.medicalConditionsOptions.indexOf('Other');
        this.medicalConditionsOptions.splice(otherIndex, 0, trimmedCondition);
        this.saveCustomMedicalConditionToStorage(trimmedCondition);
      }
      medicalConditionsValue = trimmedCondition;
    }

    // Check if complaints changed
    const originalComplaints = this.originalProgressNoteData.complaints || '';
    const currentComplaints = this.progressReviewForm.currentHealthIssues || '';
    if (originalComplaints !== currentComplaints) {
      payload.complaints = currentComplaints;
    }

    // Check if medicalConditions changed
    const originalMedicalConditions = this.originalProgressNoteData.medicalConditions || '';
    if (originalMedicalConditions !== medicalConditionsValue) {
      payload.medicalConditions = medicalConditionsValue;
    }

    // Check if createdDate (identifiedOn) changed
    const originalCreatedDate = this.normalizeDateForComparison(this.originalProgressNoteData.createdDate);
    const currentCreatedDate = this.normalizeDateForComparison(this.progressReviewForm.identifiedOn);
    if (originalCreatedDate !== currentCreatedDate && currentCreatedDate) {
      const identifiedOnDate = this.convertToISO8601(this.progressReviewForm.identifiedOn);
      if (identifiedOnDate) {
        payload.createdDate = identifiedOnDate;
      }
    }

    // Check if progressSummary changed
    const originalProgressSummary = this.originalProgressNoteData.progressSummary || '';
    const currentProgressSummary = this.progressReviewForm.progressSummary || '';
    if (originalProgressSummary !== currentProgressSummary) {
      payload.progressSummary = currentProgressSummary;
    }

    // Process goals - only include changed or new goals
    const originalGoals = this.originalProgressNoteData.goals || [];
    const currentGoals = this.progressReviewForm.goals || [];
    const updatedGoals: any[] = [];

    currentGoals.forEach((goal: any, goalIndex: number) => {
      // Handle custom goal category
      let goalCategoryValue = goal.goalCategory || '';
      if (goalCategoryValue === 'Other' && this.customGoalCategories[goalIndex]?.trim()) {
        const trimmedCategory = this.customGoalCategories[goalIndex].trim();
        if (!this.goalCategoryOptions.includes(trimmedCategory)) {
          const otherIndex = this.goalCategoryOptions.indexOf('Other');
          this.goalCategoryOptions.splice(otherIndex, 0, trimmedCategory);
          this.saveCustomGoalCategoryToStorage(trimmedCategory);
        }
        goalCategoryValue = trimmedCategory;
      }

      // Find original goal if it exists (has _id)
      const originalGoal = goal._id ? 
        originalGoals.find((g: any) => g._id === goal._id) : null;

      // If goal has ID, check if it changed; if no ID, it's a new goal
      if (!goal._id || originalGoal) {
        const goalData: any = {};

        // Always include ID if it exists
        if (goal._id) {
          goalData._id = goal._id;
        }

        // Check each goal field for changes
        if (!originalGoal || originalGoal.goalToBeAchieved !== goal.goalsToBeAchieved) {
          goalData.goalToBeAchieved = goal.goalsToBeAchieved || '';
        }
        if (!originalGoal || originalGoal.goalPriorityLevel !== goal.goalPriorityLevel) {
          goalData.goalPriorityLevel = goal.goalPriorityLevel || 'High';
        }
        if (!originalGoal || originalGoal.goalCategory !== goalCategoryValue) {
          goalData.goalCategory = goalCategoryValue;
        }

        // Check date fields
        const originalGoalStartDate = this.normalizeDateForComparison(originalGoal?.goalStartDate);
        const currentGoalStartDate = this.normalizeDateForComparison(goal.goalStartDate);
        if (originalGoalStartDate !== currentGoalStartDate && currentGoalStartDate) {
          const goalStartDate = this.convertToISO8601(goal.goalStartDate);
          if (goalStartDate) {
            goalData.goalStartDate = goalStartDate;
          }
        }

        const originalGoalTargetDate = this.normalizeDateForComparison(originalGoal?.goalTargetDate);
        const currentGoalTargetDate = this.normalizeDateForComparison(goal.goalEndDate);
        if (originalGoalTargetDate !== currentGoalTargetDate && currentGoalTargetDate) {
          const goalTargetDate = this.convertToISO8601(goal.goalEndDate);
          if (goalTargetDate) {
            goalData.goalTargetDate = goalTargetDate;
          }
        }

        // Process tasks - only include changed or new tasks
        const originalTasks = originalGoal?.tasks || [];
        const goalTasks: any[] = [];

        goal.tasks.forEach((task: any) => {
          const originalTask = task._id ? 
            originalTasks.find((t: any) => t._id === task._id) : null;

          // If task has ID, check if it changed; if no ID, it's a new task
          if (!task._id || originalTask) {
            const taskData: any = {};

            // Always include ID if it exists
            if (task._id) {
              taskData._id = task._id;
            }

            // Check each task field for changes
            if (!originalTask || originalTask.taskName !== task.task) {
              taskData.taskName = task.task || '';
            }
            if (!originalTask || originalTask.taskStatus !== task.taskStatus) {
              taskData.taskStatus = task.taskStatus || '';
            }
            if (!originalTask || originalTask.taskOwner !== task.taskOwner) {
              taskData.taskOwner = task.taskOwner || '';
            }

            // Check date fields
            const originalTaskStartDate = this.normalizeDateForComparison(originalTask?.taskStartDate);
            const currentTaskStartDate = this.normalizeDateForComparison(task.taskStartDate);
            if (originalTaskStartDate !== currentTaskStartDate && currentTaskStartDate) {
              const taskStartDate = this.convertToISO8601(task.taskStartDate);
              if (taskStartDate) {
                taskData.taskStartDate = taskStartDate;
              }
            }

            const originalTaskEndDate = this.normalizeDateForComparison(originalTask?.taskEndDate);
            const currentTaskEndDate = this.normalizeDateForComparison(task.taskEndDate);
            if (originalTaskEndDate !== currentTaskEndDate && currentTaskEndDate) {
              const taskEndDate = this.convertToISO8601(task.taskEndDate);
              if (taskEndDate) {
                taskData.taskEndDate = taskEndDate;
              }
            }

            // Only add task if it has changes or is new
            if (!task._id || Object.keys(taskData).length > 1) { // More than just _id
              goalTasks.push(taskData);
            }
          }
        });

        // Only include tasks if there are any
        if (goalTasks.length > 0) {
          goalData.tasks = goalTasks;
        }

        // Only add goal if it has changes or is new
        if (!goal._id || Object.keys(goalData).length > 1) { // More than just _id
          updatedGoals.push(goalData);
        }
      }
    });

    // Only include goals if there are any
    if (updatedGoals.length > 0) {
      payload.goals = updatedGoals;
    }

    // Include removed goals/tasks with _id and message so backend can delete them
    if (this.deletedGoalsToSend.length > 0) {
      payload.deletedGoals = this.deletedGoalsToSend;
    }
    if (this.deletedTasksToSend.length > 0) {
      payload.deletedTasks = this.deletedTasksToSend;
    }

    return payload;
  }

  private prepareNewProgressNotePayload(): any {
    const currentDate = new Date().toISOString();

    // Handle custom medical condition
    let medicalConditionsValue = this.progressReviewForm.medicalConditions || '';
    if (medicalConditionsValue === 'Other' && this.customMedicalCondition.trim()) {
      const trimmedCondition = this.customMedicalCondition.trim();
      if (!this.medicalConditionsOptions.includes(trimmedCondition)) {
        const otherIndex = this.medicalConditionsOptions.indexOf('Other');
        this.medicalConditionsOptions.splice(otherIndex, 0, trimmedCondition);
        this.saveCustomMedicalConditionToStorage(trimmedCondition);
      }
      medicalConditionsValue = trimmedCondition;
    }

    const identifiedOnDate = this.convertToISO8601(this.progressReviewForm.identifiedOn);
    const createdDateValue = identifiedOnDate || currentDate;

    return {
      memberId: this.memberId,
      complaints: this.progressReviewForm.currentHealthIssues || '',
      medicalConditions: medicalConditionsValue,
      createdDate: createdDateValue,
      modifiedDate: currentDate,
      goals: this.progressReviewForm.goals.map((goal, index) => {
        // Handle custom goal category
        let goalCategoryValue = goal.goalCategory || '';
        if (goalCategoryValue === 'Other' && this.customGoalCategories[index]?.trim()) {
          const trimmedCategory = this.customGoalCategories[index].trim();
          if (!this.goalCategoryOptions.includes(trimmedCategory)) {
            const otherIndex = this.goalCategoryOptions.indexOf('Other');
            this.goalCategoryOptions.splice(otherIndex, 0, trimmedCategory);
            this.saveCustomGoalCategoryToStorage(trimmedCategory);
          }
          goalCategoryValue = trimmedCategory;
        }

        const goalData: any = {
          goalToBeAchieved: goal.goalsToBeAchieved || '',
          goalPriorityLevel: goal.goalPriorityLevel || 'High',
          goalCategory: goalCategoryValue,
          tasks: goal.tasks.map(task => {
            const taskData: any = {
              taskName: task.task || '',
              taskStatus: task.taskStatus || '',
              taskOwner: task.taskOwner || ''
            };

            const taskStartDate = this.convertToISO8601(task.taskStartDate);
            if (taskStartDate) {
              taskData.taskStartDate = taskStartDate;
            }

            const taskEndDate = this.convertToISO8601(task.taskEndDate);
            if (taskEndDate) {
              taskData.taskEndDate = taskEndDate;
            }

            return taskData;
          })
        };

        const goalStartDate = this.convertToISO8601(goal.goalStartDate);
        if (goalStartDate) {
          goalData.goalStartDate = goalStartDate;
        }

        const goalTargetDate = this.convertToISO8601(goal.goalEndDate);
        if (goalTargetDate) {
          goalData.goalTargetDate = goalTargetDate;
        }

        return goalData;
      }),
      progressSummary: this.progressReviewForm.progressSummary || '',
      isArchived: false
    };
  }

  cancelProgressReview(): void {
    this.showProgressReviewForm = false;
    this.isEditingExistingNote = false;

    // If viewing from Completed tab, navigate back to it
    if (this.isViewingFromCompletedTab) {
      this.activeProgressTab = 'completed';
      this.isViewingFromCompletedTab = false;
    }

    // Clear current viewed row and original data
    this.currentViewedRow = null;
    this.originalProgressNoteData = null;
  }

  markProgressNoteComplete(): void {
    console.log('Marking progress note as complete:', this.currentViewedRow);

    if (!this.currentViewedRow || !this.currentViewedRow.rawData || !this.currentViewedRow.rawData.id) {
      console.error('Cannot mark as complete: missing record ID');
      alert('Cannot mark this record as complete. Missing record ID.');
      return;
    }

    const updateData = {
      isCompleted: true,
      modifiedDate: new Date().toISOString()
    };

    this.memberService.updateProgressNotes(this.currentViewedRow.rawData.id, updateData).subscribe({
      next: (response) => {
        console.log('Item marked as complete successfully via API:', response);

        // Update local data after successful API call
        if (this.currentViewedRow.rawData) {
          this.currentViewedRow.rawData.isCompleted = true;
          this.currentViewedRow.isCompleted = true;
        }

        // Get current data sources
        const priorityData = [...this.dataSource.data];
        const archivedData = [...this.archivedDataSource.data];
        const completedData = [...this.completedDataSource.data];

        // Remove from Priority List if exists
        const priorityIndex = priorityData.findIndex(item => item.id === this.currentViewedRow.id);
        if (priorityIndex !== -1) {
          priorityData.splice(priorityIndex, 1);
        }

        // Remove from Archived List if exists
        const archivedIndex = archivedData.findIndex(item => item.id === this.currentViewedRow.id);
        if (archivedIndex !== -1) {
          archivedData.splice(archivedIndex, 1);
        }

        // Add to Completed List
        completedData.push(this.currentViewedRow);

        // Sort by Identified On (latest first)
        this.sortByIdentifiedOn(priorityData);
        this.sortByIdentifiedOn(archivedData);
        this.sortByIdentifiedOn(completedData);

        // Update the data sources
        this.dataSource.data = priorityData;
        this.archivedDataSource.data = archivedData;
        this.completedDataSource.data = completedData;

        // Update record counts
        this.totalRecords = priorityData.length;
        this.archivedTotalRecords = archivedData.length;
        this.completedTotalRecords = completedData.length;

        // Reconnect paginators after data update
        this.connectPaginators();

        console.log('Priority items:', this.totalRecords, 'Archived items:', this.archivedTotalRecords, 'Completed items:', this.completedTotalRecords);

        // Close the review form and navigate to Completed tab
        this.showProgressReviewForm = false;
        this.isEditingExistingNote = false;
        this.activeProgressTab = 'completed';
        this.currentViewedRow = null;
      },
      error: (error) => {
        console.error('Error marking item as complete:', error);
        alert('Failed to mark item as complete. Please try again.');
      }
    });
  }

  markProgressNoteIncomplete(): void {
    console.log('Marking progress note as incomplete:', this.currentViewedRow);

    if (!this.currentViewedRow || !this.currentViewedRow.rawData || !this.currentViewedRow.rawData.id) {
      console.error('Cannot mark as incomplete: missing record ID');
      alert('Cannot mark this record as incomplete. Missing record ID.');
      return;
    }

    const updateData = {
      isCompleted: false,
      modifiedDate: new Date().toISOString()
    };

    this.memberService.updateProgressNotes(this.currentViewedRow.rawData.id, updateData).subscribe({
      next: (response) => {
        console.log('Item marked as incomplete successfully via API:', response);

        // Update local data after successful API call
        if (this.currentViewedRow.rawData) {
          this.currentViewedRow.rawData.isCompleted = false;
          this.currentViewedRow.isCompleted = false;
        }

        // Get current data sources
        const priorityData = [...this.dataSource.data];
        const archivedData = [...this.archivedDataSource.data];
        const completedData = [...this.completedDataSource.data];

        // Remove from Completed List
        const completedIndex = completedData.findIndex(item => item.id === this.currentViewedRow.id);
        if (completedIndex !== -1) {
          completedData.splice(completedIndex, 1);
        }

        // Add to Priority List or Archived List based on isArchived flag
        if (this.currentViewedRow.rawData.isArchived) {
          archivedData.push(this.currentViewedRow);
        } else {
          priorityData.push(this.currentViewedRow);
        }

        // Sort by Identified On (latest first)
        this.sortByIdentifiedOn(priorityData);
        this.sortByIdentifiedOn(archivedData);
        this.sortByIdentifiedOn(completedData);

        // Update the data sources
        this.dataSource.data = priorityData;
        this.archivedDataSource.data = archivedData;
        this.completedDataSource.data = completedData;

        // Update record counts
        this.totalRecords = priorityData.length;
        this.archivedTotalRecords = archivedData.length;
        this.completedTotalRecords = completedData.length;

        // Reconnect paginators after data update
        this.connectPaginators();

        console.log('Priority items:', this.totalRecords, 'Archived items:', this.archivedTotalRecords, 'Completed items:', this.completedTotalRecords);

        // Close the review form and navigate back to Completed tab
        this.showProgressReviewForm = false;
        this.isEditingExistingNote = false;
        this.isViewingFromCompletedTab = false;
        this.activeProgressTab = 'completed';
        this.currentViewedRow = null;
      },
      error: (error) => {
        console.error('Error marking item as incomplete:', error);
        alert('Failed to mark item as incomplete. Please try again.');
      }
    });
  }

  // Success Modal methods
  continueFromSuccess(): void {
    console.log('Continue clicked - navigating back to tab:', this.successModalSourceTab);

    // Hide success modal
    this.showSuccessModal = false;

    // Navigate back to the appropriate tab
    this.activeProgressTab = this.successModalSourceTab;

    // Hide all forms
    this.showAddNotesForm = false;
    this.showProgressReviewForm = false;
    this.isEditingExistingNote = false;

    // Clear custom values
    this.customMedicalCondition = '';
    this.customGoalCategories = {};

    // Reload data to show updated information
    this.loadMemberIssues();
  }

  // Task Status Dropdown Methods
  toggleStatusDropdown(goalIndex: number, taskIndex: number): void {
    if (this.isStatusDropdownOpen(goalIndex, taskIndex)) {
      this.openStatusDropdown = null;
    } else {
      this.openStatusDropdown = { goalIndex, taskIndex };
    }
  }

  isStatusDropdownOpen(goalIndex: number, taskIndex: number): boolean {
    return this.openStatusDropdown?.goalIndex === goalIndex &&
      this.openStatusDropdown?.taskIndex === taskIndex;
  }

  selectTaskStatus(goalIndex: number, taskIndex: number, status: string): void {
    const task = this.progressNotesForm.goals[goalIndex].tasks[taskIndex];
    const previousStatus = task.taskStatus;
    task.taskStatus = status;

    // If changing from Ongoing to another status, clear the end date if it's invalid
    if (previousStatus === 'Ongoing' && status !== 'Ongoing') {
      // Clear end date to allow user to select a new one
      task.taskEndDate = '';
    }

    // If changing to Ongoing, clear the end date since Ongoing doesn't use it
    if (status === 'Ongoing') {
      task.taskEndDate = '';
    }

    this.openStatusDropdown = null;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.custom-status-dropdown')) {
      this.openStatusDropdown = null;
    }
    // Close health issue dropdown when clicking outside
    if (!target.closest('.health-issue-dropdown-container')) {
      this.openHealthIssueDropdown = null;
    }
  }

  // Health Issue Dropdown Methods
  toggleHealthIssueDropdown(rowId: string, event: Event): void {
    event.stopPropagation();
    if (this.openHealthIssueDropdown === rowId) {
      this.openHealthIssueDropdown = null;
    } else {
      this.openHealthIssueDropdown = rowId;
    }
  }

  isHealthIssueDropdownOpen(rowId: string): boolean {
    return this.openHealthIssueDropdown === rowId;
  }

  // Date validation methods
  validateIdentifiedOnDate(): void {
    if (this.progressNotesForm.identifiedOn) {
      const selectedDate = new Date(this.progressNotesForm.identifiedOn);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      selectedDate.setHours(0, 0, 0, 0);

      if (selectedDate > today) {
        this.identifiedOnError = 'Future dates are not allowed for Complaint Identified Date.';
      } else {
        this.identifiedOnError = '';
      }
    } else {
      this.identifiedOnError = '';
    }
  }

  validateGoalTargetDate(goalIndex: number): void {
    const goal = this.progressNotesForm.goals[goalIndex];
    if (goal.goalStartDate && goal.goalTargetDate) {
      const startDate = new Date(goal.goalStartDate);
      const targetDate = new Date(goal.goalTargetDate);

      if (targetDate < startDate) {
        this.goalDateErrors[goalIndex] = 'End Date cannot be earlier than Start Date.';
      } else {
        delete this.goalDateErrors[goalIndex];
      }
    } else {
      delete this.goalDateErrors[goalIndex];
    }
  }

  validateTaskEndDate(goalIndex: number, taskIndex: number): void {
    const task = this.progressNotesForm.goals[goalIndex].tasks[taskIndex];
    const key = `${goalIndex}-${taskIndex}`;

    if (task.taskStartDate && task.taskEndDate) {
      const startDate = new Date(task.taskStartDate);
      const endDate = new Date(task.taskEndDate);

      if (endDate < startDate) {
        this.taskDateErrors[key] = 'End Date cannot be earlier than Start Date.';
      } else {
        delete this.taskDateErrors[key];
      }
    } else {
      delete this.taskDateErrors[key];
    }
  }

  getTaskDateErrorKey(goalIndex: number, taskIndex: number): string {
    return `${goalIndex}-${taskIndex}`;
  }

  hasTaskDateError(goalIndex: number, taskIndex: number): boolean {
    const key = this.getTaskDateErrorKey(goalIndex, taskIndex);
    return !!this.taskDateErrors[key];
  }

  getTaskDateError(goalIndex: number, taskIndex: number): string {
    const key = this.getTaskDateErrorKey(goalIndex, taskIndex);
    return this.taskDateErrors[key] || '';
  }

  // Check if there are any validation errors
  hasAnyValidationErrors(): boolean {
    // Check identified on error
    if (this.identifiedOnError) {
      return true;
    }

    // Check goal date errors
    if (Object.keys(this.goalDateErrors).length > 0) {
      return true;
    }

    // Check task date errors
    if (Object.keys(this.taskDateErrors).length > 0) {
      return true;
    }

    return false;
  }
}